import { useState } from "react"
import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"

type Props = {
  value: string
  rotateUrl?: string                // optional — when present, exposes a "Rotate" action
}

// Sensitive-string field with show/hide toggle + copy-to-clipboard.
// Show/hide is a UI affordance, NOT a security boundary — the full string
// is in props (network + DOM). Treat it like Stripe's dashboard does.
export function ApiKeyField({ value, rotateUrl }: Props) {
  const [shown, setShown] = useState(false)
  const [copied, setCopied] = useState(false)

  const masked = value.length > 8
    ? `${value.slice(0, 4)}${"•".repeat(Math.max(value.length - 8, 8))}${value.slice(-4)}`
    : "••••••••"

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard not available — silent
    }
  }

  function rotate() {
    if (!rotateUrl) return
    if (!confirm("Rotate the API key? Any client using the current value will start failing immediately.")) return
    router.post(rotateUrl)
  }

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <code className="flex-1 truncate rounded-control border border-border bg-white/5 px-3 py-2 font-mono text-sm text-app-text">
          {shown ? value : masked}
        </code>
        <Button variant="ghost" size="sm" type="button" onClick={() => setShown((v) => !v)}>
          {shown ? "Hide" : "Show"}
        </Button>
        <Button variant="ghost" size="sm" type="button" onClick={copy}>
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      {rotateUrl && (
        <Button variant="gold" size="sm" type="button" onClick={rotate}>
          Rotate API key
        </Button>
      )}
    </div>
  )
}
